/**
 * Assert that a code path is unreachable.
 *
 * Equivalent to `absurd` in FP paradygms.
 *
 * Use this function after exhaustive checks where the type system has narrowed
 * a value to `never`. By the logical principle "ex falso quodlibet" (from an
 * impossible value anything follows), this function is given a polymorphic
 * result type so it can be used in any expected type context.
 *
 * @example
 *  This is particularly useful for exhaustive checks with discriminated unions:
 *  ```ts
 *  type Shape =
 *    | { _tag: 'Circle'; radius: number }
 *    | { _tag: 'Square'; side: number }
 *
 *  function area(shape: Shape): number {
 *    switch (shape._tag) {
 *      case 'Circle':
 *        return Math.PI * shape.radius ** 2
 *      case 'Square':
 *        return s.side ** 2
 *    }
 *
 *    return assertUnreachable(s)
 *    //     ^? never
 *    // If all cases are handled, `s` is of type `never`,
 *    // and Typescript understands that the return never occurs.
 *    // If there was a missing case, Typescript would complain with error:
 *    // `<typeof s> is not assignable to type `never`` (error at compile time).
 *  }
 *  ```
 * @template T - The target result type. This type parameter is only used at
 *  compile time: `absurd` will never produce a value at
 *  runtime (it always throws). In practice `T` is inferred from
 *  the surrounding context so no explicit annotation is usually
 *  required.
 *
 * @param value - A value of type `never` (uninhabited).
 *  Passing a non-`never` value is a type error.
 *
 * @throws {Error}
 *  **ALWAYS THROWS** an Error to indicate an unreachable code path.
 *  `Error#cause` being the impossible/faulty value.
 */
export const assertUnreachable = <T>(value: never): T => {
  throw new Error(
    'Reached an unexpected code path. This is most likely developer error.',
    {
      cause: value,
    },
  )
}
